// src/pages/leaderManagement/leaderList/leaderList.js
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination, Table, Tooltip } from "antd";
import { FaEye } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./leaderList.css";

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  leaderData: [],
  currentlyEditingId: null,
  currentPage: 1,
  pageSize: 10,
  sortConfig: null
};

// Reducer for state management
const leaderReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setLeaderData":
      return { ...state, leaderData: action.payload };
    case "setEditingId":
      return { ...state, currentlyEditingId: action.payload };
    case "updateLeaderStatus":
      return {
        ...state,
        leaderData: state.leaderData.map((item) => (item.userId === action.payload.userId ? { ...item, role: action.payload.role } : item)),
        currentlyEditingId: null
      };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload, currentPage: 1 };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function LeaderList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [state, dispatch] = useReducer(leaderReducer, initialState);

  // State for immediate search display
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const { isLoading, searchTerm, filters, leaderData, currentlyEditingId, currentPage, pageSize, sortConfig } = state;

  // Helper function for activity status
  const getLastActiveTime = useCallback((lastAdminLoginAt) => {
    if (!lastAdminLoginAt) return "No Data";

    const now = new Date();
    const loginTime = new Date(lastAdminLoginAt);
    const diffInMinutes = Math.floor((now - loginTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Active now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  }, []);

  // Get activity status based on lastAdminLoginAt
  const getActivityStatus = useCallback((lastAdminLoginAt) => {
    if (!lastAdminLoginAt) return "Inactive Leader";

    const now = new Date();
    const loginTime = new Date(lastAdminLoginAt);
    const diffInDays = Math.floor((now - loginTime) / (1000 * 60 * 60 * 24));

    if (diffInDays <= 7) return "Active Leader";
    return "Inactive Leader";
  }, []);

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      activityStatus: {
        label: "Activity Status",
        options: [
          { value: "Active Leader", label: "Active Leader" },
          { value: "Inactive Leader", label: "Inactive Leader" }
        ]
      }
    }),
    []
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.name || "",
        item.email || "",
        item.phoneNumber || "",
        item.role || "",
        item.createdAt
          ? new Date(item.createdAt)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters, search, and sorting in correct order
  const filteredData = useMemo(() => {
    let result = [...leaderData];

    // Step 1: Apply activity status filter
    if (filters.activityStatus) {
      result = result.filter((item) => {
        const status = getActivityStatus(item.lastAdminLoginAt);
        return status === filters.activityStatus;
      });
    }

    // Step 2: Apply global search
    result = globalSearch(result, searchTerm);

    // Step 3: Apply sorting BEFORE pagination (CRITICAL FIX)
    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;

        if (key === "lastAdminLoginAt") {
          const dateA = a.lastAdminLoginAt ? new Date(a.lastAdminLoginAt) : new Date(0);
          const dateB = b.lastAdminLoginAt ? new Date(b.lastAdminLoginAt) : new Date(0);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Return fully processed data (filtered, searched, and sorted)
    return result;
  }, [leaderData, filters, searchTerm, globalSearch, sortConfig, getActivityStatus]);

  // Get current page data (pagination happens AFTER all processing)
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fixed view button handler
  const handleView = useCallback(
    (userId) => {
      navigate(`/leaderDetails/${userId}`);
    },
    [navigate]
  );

  // Debounced search dispatch
  const debouncedDispatch = useMemo(
    () =>
      debounce((term) => {
        dispatch({ type: "setSearchTerm", payload: term });
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Handle table change (sorting)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter.order) {
      dispatch({
        type: "setSortConfig",
        payload: {
          key: sorter.field,
          direction: sorter.order === "ascend" ? "asc" : "desc"
        }
      });
    } else {
      dispatch({
        type: "setSortConfig",
        payload: null
      });
    }
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "NAME",
        dataIndex: "name",
        sorter: true,
        sortOrder: sortConfig?.key === "name" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => (
          <div className="ldl-name-email">
            <div className="ldl-name">{record.name || "-"}</div>
            <div className="ldl-email">{record.email || "-"}</div>
          </div>
        )
      },
      {
        title: "CONTACT INFO",
        dataIndex: "phoneNumber",
        render: (phone) => phone || "-"
      },
      {
        title: "ROLE",
        dataIndex: "role",
        sorter: true,
        sortOrder: sortConfig?.key === "role" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (role) => role || "-"
      },
      {
        title: "ACTIVITY STATUS",
        dataIndex: "lastAdminLoginAt",
        sorter: true,
        sortOrder: sortConfig?.key === "lastAdminLoginAt" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (lastAdminLoginAt, record) => (
          <div className="ldl-login-status">
            <span>
              {lastAdminLoginAt
                ? new Date(lastAdminLoginAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                  })
                : "Inactive"}
            </span>
            <span className={`ldl-status-text ${getLastActiveTime(lastAdminLoginAt) === "Active now" ? "active" : ""}`}>{getLastActiveTime(lastAdminLoginAt)}</span>
          </div>
        )
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="ldl-action-buttons" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View">
              <button className="ldl-table-view-btn" onClick={() => handleView(record.userId)}>
                <FaEye />
              </button>
            </Tooltip>
          </div>
        ),
        width: 80
      }
    ],
    [currentPage, pageSize, sortConfig, handleView, getLastActiveTime]
  );

  // Effects
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        dispatch({ type: "setEditingId", payload: null });
      }
    };

    if (currentlyEditingId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [currentlyEditingId]);

  useEffect(() => {
    let isMounted = true;

    const fetchLeaders = async () => {
      dispatch({ type: "setLoading", payload: true });

      try {
        const token = await getAuthToken();

        const response = await axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isMounted) return;

        // Filter users with an assigned role (non-empty, not "user")
        const assignedLeaders = (response.data.users || []).filter((user) => user.role && user.role !== "" && user.role !== "user");

        dispatch({
          type: "setLeaderData",
          payload: assignedLeaders
        });
      } catch (err) {
        console.error("GET error:", err);

        if (isMounted) {
          dispatch({
            type: "setLeaderData",
            payload: []
          });
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchLeaders();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((leader) => ({
      "Leader ID": leader.userId,
      "Full Name": leader.name,
      Email: leader.email,
      "Phone Number": leader.phoneNumber,
      Role: leader.role,
      "Activity Status": getLastActiveTime(leader.lastAdminLoginAt),
      "Registration Date": leader.createdAt,
      Status: leader.activityStatus
    }));
  }, [filteredData, getLastActiveTime]);

  return (
    <Layout
      navTitle="Leader Management"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="leader-list"
            excelSheetName="Leaders"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.userId}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
          onChange={handleTableChange}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>
    </Layout>
  );
}

export default LeaderList;
